/**
 * Restores the market's display symbol on the prices APO formats itself.
 *
 * APO renders its money strings from `mwApoInit.shop_money_format`, which is the
 * SHOP's format. A Hong Kong shopper therefore reads "$2,032.00" where the theme
 * prints "HK$2,032.00": the amount is already in the presentment currency, only
 * the symbol disagrees.
 *
 * This swaps the symbol token and nothing else. Re-formatting the value would
 * mean re-parsing APO's output, and those strings carry a sign, grouping
 * separators, surrounding brackets and a separate struck-through compare price.
 *
 * The replacement is read from the node the theme renders its own base price
 * into. That node is written by snippets/lp-currency-symbol.liquid, so the Liquid
 * table stays the only source of truth and this file adds no second copy to keep
 * in sync. It also keeps the fix working while assets/lp-money.js is not loaded.
 *
 * Scope is the product section, and only APO-owned nodes are named. Everything
 * else on the page — including .lp-frame-rule-price, which the theme already
 * writes with the correct symbol — is left untouched.
 */
(function () {
  'use strict';

  if (window.__LP_APO_CURRENCY_SYMBOL__) return;
  window.__LP_APO_CURRENCY_SYMBOL__ = true;

  var INFO_SELECTOR = '.main-product .product__info-container';

  // APO owns all three: the surcharge under an option card, its struck-through
  // original, and the running total APO moves above the Next Step button.
  var APO_PRICE_SELECTOR =
    '.mw-option__price, .mw-option__compare-price, .price__sale .price-item--sale';

  // Where the theme prints its own base price. APO never writes this node, and
  // CSS only hides it on the portrait PDP, so it stays readable as a source.
  var THEME_PRICE_SELECTOR = '.price__regular .price-item--regular';

  // A currency token sitting immediately before the first digit: "$", "HK$",
  // "JP¥". The negated class steps over the sign and brackets around it, and
  // stops at whitespace so the translated "From" in "From HK$2,032.00" is never
  // swallowed along with the symbol.
  var SYMBOL_BEFORE_NUMBER = /([^\d\s.,+\-()%]{1,3})\s*(?=\d)/;

  // Only tokens carrying a currency glyph are accepted. A bare uppercase run can
  // be a word, and a word rewritten into a symbol is far worse than a symbol
  // left alone; every currency this storefront prices in carries a glyph.
  var CURRENCY_TOKEN = /^[A-Z]{0,2}[$€£¥₩]$/;

  var symbolCache = '';

  function themeSymbol() {
    if (symbolCache) return symbolCache;

    var nodes = document.querySelectorAll(THEME_PRICE_SELECTOR);
    for (var i = 0; i < nodes.length; i += 1) {
      var match = nodes[i].textContent.match(SYMBOL_BEFORE_NUMBER);
      if (match && CURRENCY_TOKEN.test(match[1])) {
        // Cached only on success: an empty result means the node is not in the
        // DOM yet, and the next pass has to be free to look again.
        symbolCache = match[1];
        return symbolCache;
      }
    }

    return '';
  }

  function rewrite(node, symbol) {
    // These nodes hold a single text node by construction. Anything else means
    // APO changed shape, and flattening an unknown node would destroy markup.
    if (node.childNodes.length !== 1 || node.childNodes[0].nodeType !== 3) {
      return;
    }

    var textNode = node.childNodes[0];
    var match = textNode.nodeValue.match(SYMBOL_BEFORE_NUMBER);
    if (!match || !CURRENCY_TOKEN.test(match[1]) || match[1] === symbol) return;

    var start = match.index;
    textNode.nodeValue =
      textNode.nodeValue.slice(0, start) +
      symbol +
      textNode.nodeValue.slice(start + match[1].length);
  }

  function apply() {
    var info = document.querySelector(INFO_SELECTOR);
    if (!info) return;

    var symbol = themeSymbol();
    if (!symbol) return;

    info.querySelectorAll(APO_PRICE_SELECTOR).forEach(function (node) {
      rewrite(node, symbol);
    });
  }

  function bind() {
    var info = document.querySelector(INFO_SELECTOR);
    if (!info) return;

    if ('MutationObserver' in window) {
      // APO rebuilds these on every option change. The cycle settles after one
      // extra pass, because rewrite() returns as soon as the symbol matches.
      new MutationObserver(function () {
        // Restore the symbol before paint, so "$" cannot flash before "HK$".
        apply();
      }).observe(info, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    ['mwApo::ready', 'mwApo::recalculated', 'luminipet:apo-ready'].forEach(
      function (name) {
        document.addEventListener(name, function () {
          apply();
        });
      }
    );

    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();
